/**
 * The control laws every AI manoeuvre is written in terms of. These produce
 * ordinary `FlightInputs` — stick, rudder, throttle — and nothing else, which
 * is the whole point: the AI flies the same `step()` the player does, through
 * the same aerodynamics, with no helper forces and no attitude teleports. If a
 * Camel cannot hold a manoeuvre, neither can the AI flying one.
 *
 * Gains are named tunables (`aiRollGain`, `aiPitchGain`, `aiRudderGain`,
 * `aiRollDamping`), all CALIBRATION-PENDING.
 */

import { standardAtmosphere } from "../flight/atmosphere.ts";
import { NEUTRAL_INPUTS, type FlightInputs } from "../flight/inputs.ts";
import { bankAngle, pitchAngle } from "../flight/math3d.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { GRAVITY_MPS2, clamp } from "../flight/units.ts";

/** Wraps into (−π, π] so a bank command never takes the long way round. */
export function wrapAngle(radians: number): number {
  const wrapped = (radians + Math.PI) % (2 * Math.PI);
  return (wrapped < 0 ? wrapped + 2 * Math.PI : wrapped) - Math.PI;
}

/** Aileron demand to reach `targetBankRad`, damped on the current roll rate. */
export function rollToBank(sim: SimState, targetBankRad: number, tunables: Tunables): number {
  const error = wrapAngle(targetBankRad - bankAngle(sim.orientation));
  return clamp(error * tunables.aiRollGain - sim.rates.roll * tunables.aiRollDamping, -1, 1);
}

/**
 * Elevator demand to hold an angle of attack. Working in α rather than pitch
 * attitude is what keeps the AI on the right side of the stall break while
 * still pulling as hard as the airframe allows.
 */
export function pitchToAlpha(sim: SimState, targetAlphaRad: number, tunables: Tunables): number {
  return clamp((targetAlphaRad - sim.readout.alphaRad) * tunables.aiPitchGain, -1, 1);
}

/**
 * Elevator demand to hold a flight-path-independent pitch attitude.
 *
 * **What AD-007o's wing incidence did to every constant fed to this**, because
 * it is not obvious and the answer is "made them mean what they say". The
 * function is in attitude, so nothing here moved; what moved is the flight path
 * a given attitude buys. Before the incidence a commanded 0 was a Camel sinking
 * ~400 ft/min, so "hold level" was a slow descent and the surrounding code had
 * been tuned around that; a commanded 0 now climbs ~230 ft/min, which is what
 * the original does at 0.00° of pitch (`re-sessions/calib` §4.1).
 *
 * The large attitudes — `CLIMB_OUT_PITCH_RAD` 8°, `DIVE_ATTITUDE_RAD` −40°,
 * `ZOOM_ATTITUDE_RAD` +37° — are named for shapes rather than for trims and a
 * 5.5° shift does not change which shape they are, so they are left alone
 * rather than re-tuned without evidence. A lane that does want to re-tune them
 * should do it against the AI's own rails (the duel and mission censuses), not
 * against this arithmetic.
 */
export function pitchToAttitude(sim: SimState, targetPitchRad: number, tunables: Tunables): number {
  const error = wrapAngle(targetPitchRad - pitchAngle(sim.orientation));
  return clamp(error * tunables.aiPitchGain * 0.25 - sim.rates.pitch * 0.4, -1, 1);
}

/** Rudder demand that zeroes sideslip — the AI coordinating its own turn. */
export function coordinateRudder(sim: SimState, tunables: Tunables): number {
  return clamp(sim.readout.betaRad * tunables.aiRudderGain, -1, 1);
}

/** The hardest pull this airframe can sustain without breaking into the stall. */
export function pullAlphaRad(sim: SimState, tunables: Tunables): number {
  return sim.config.spec.aero.alphaStallRad * tunables.aiPullAlphaFraction;
}

/** Speed as a fraction of this airframe's own top speed — the AI's energy gauge. */
export function speedFraction(sim: SimState): number {
  return sim.readout.airspeedMps / Math.max(sim.config.spec.vmaxMps, 1);
}

/** Below this fraction of Vmax the AI stops pulling and flies for speed instead. */
const MUSHING_SPEED_FRACTION = 0.3;
/** At and above this fraction it will pull as hard as the wing allows. */
const MANOEUVRING_SPEED_FRACTION = 0.45;

/**
 * The pull the AI *should* be asking for, given how much speed it has left.
 * Hauling the stick back at 25 mph does not turn a Camel, it hangs it on the
 * prop — and two aeroplanes doing that to each other is a slow-motion scissors
 * that neither can shoot from. Below `MUSHING_SPEED_FRACTION` the demand
 * collapses, which drops the nose and buys the speed back.
 */
export function energyLimitedAlpha(sim: SimState, tunables: Tunables): number {
  const span = MANOEUVRING_SPEED_FRACTION - MUSHING_SPEED_FRACTION;
  const scale = clamp((speedFraction(sim) - MUSHING_SPEED_FRACTION) / span, 0.1, 1);
  return pullAlphaRad(sim, tunables) * scale;
}

/** Steepest and shallowest bank the limiter will ever command, radians. */
const MIN_SUSTAINABLE_BANK_RAD = 0.6;
const MAX_SUSTAINABLE_BANK_RAD = 1.35;

/**
 * The steepest bank this aircraft can hold *right now* without the turn
 * degenerating. A level turn at bank φ needs `1/cos φ` g; the wing can only
 * give `½ρV²S·CLmax / mg`. Banking past that is the exact mistake the
 * Intermediate and Expert tiers punish with the manual's "slow spiral dive", so
 * the AI checks its own energy before hauling the stick over — which is the
 * difference between a pilot and a controller.
 */
export function sustainableBankRad(sim: SimState): number {
  const { spec } = sim.config;
  const air = standardAtmosphere(sim.config.altitudeLapse ? sim.position.y : 0);
  const speed = Math.max(sim.readout.airspeedMps, 1);
  const maxLift = 0.5 * air.densityKgM3 * speed * speed * spec.aero.wingAreaM2 * spec.aero.clMax;
  const loadFactor = maxLift / Math.max(spec.massKg * GRAVITY_MPS2, 1);
  if (loadFactor <= 1.05) return MIN_SUSTAINABLE_BANK_RAD;
  return clamp(Math.acos(1 / loadFactor), MIN_SUSTAINABLE_BANK_RAD, MAX_SUSTAINABLE_BANK_RAD);
}

/** Keeps a commanded bank's direction but never asks for more than `limitRad` of it. */
export function limitBank(bankRad: number, limitRad: number): number {
  const wrapped = wrapAngle(bankRad);
  return Math.sign(wrapped) * Math.min(Math.abs(wrapped), limitRad);
}

export function inputs(overrides: Partial<FlightInputs>): FlightInputs {
  return { ...NEUTRAL_INPUTS, ...overrides };
}

/**
 * A wings-level, nose-up recovery: what the AI reverts to when it is stalled,
 * spinning, or about to fly into the ground. The spin case uses the sim's own
 * recovery inputs (opposite rudder, forward stick — spin.ts), because that is
 * what actually breaks autorotation in this model.
 */
export function recoveryInputs(sim: SimState, tunables: Tunables): FlightInputs {
  if (sim.stallModes.spin.active) {
    // Full deflection rather than exactly the threshold: `spin.ts` tests the
    // inputs against `spinRecoveryRudderInput`/`spinRecoveryStickInput`, so
    // sitting on the boundary would make recovery depend on rounding.
    return inputs({ pitch: -1, yaw: -sim.stallModes.spin.direction, throttle: 0.2 });
  }
  if (sim.readout.stalled) {
    return inputs({
      pitch: -0.4,
      roll: rollToBank(sim, 0, tunables),
      yaw: coordinateRudder(sim, tunables),
      throttle: 1,
    });
  }
  return inputs({
    pitch: pitchToAlpha(sim, pullAlphaRad(sim, tunables) * 0.7, tunables),
    roll: rollToBank(sim, 0, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/** How far ahead the AI looks for the ground, in seconds of current descent rate. */
const GROUND_LOOKAHEAD_SECONDS = 4;

/**
 * True when the aircraft needs the ground more than it needs the fight. The
 * altitude test is predictive rather than instantaneous: a scout going straight
 * down at 70 m/s that only reacts *at* the clearance height has already lost.
 *
 * Height is read **above the terrain** (`readout.altitudeAglM`) rather than
 * above datum, so the same clearance means the same thing over a hill as over a
 * field once the elevation lane's `GroundQuery` is wired in. An aeroplane whose
 * wheels are still on the ground is not recovering from anything — it is taking
 * off, which `takingOff`/`takeoffInputs` below own.
 */
export function needsRecovery(sim: SimState, tunables: Tunables): boolean {
  // An aeroplane still on its wheels is not recovering from anything — it is
  // taking off, which `takingOff`/`takeoffInputs` below own.
  if (takingOff(sim)) return false;
  if (sim.stallModes.spin.active || sim.readout.stalled) return true;
  const agl = sim.readout.altitudeAglM;
  const predicted = agl + sim.readout.climbRateMps * GROUND_LOOKAHEAD_SECONDS;
  return Math.min(agl, predicted) < tunables.aiGroundClearanceM;
}

/**
 * True while the aeroplane still has business on the ground. The Mission
 * Builder manual's scramble recipe — "start the group at zero altitude and keep
 * the group's second path point above the aerodrome but at a higher altitude.
 * The group will rise to the specified height and circle" — is the whole of the
 * original's AI ground behaviour: it takes off and it climbs away. It never
 * lands ("You can't make non-player aircraft land. Instead, they will circle
 * overhead and allow the player to land."), and neither does this one.
 */
export function takingOff(sim: SimState): boolean {
  return sim.gear.onGround && !sim.gear.collapsed;
}

/**
 * Whether a take-off already under way is still under way. Called only from the
 * AI's `takeoff` mode, which is what supplies the history this cannot see: the
 * test is "not yet clear of the ground", and *any* aeroplane low over a field
 * would answer yes to that, which is why it is not a mode entry condition.
 *
 * The height it hands over at is `aiGroundClearanceM` — the same height the
 * ground-avoidance law stops worrying at. Sharing one number is what keeps the
 * two from handing back and forth: at anything lower, `needsRecovery` would
 * take a fresh take-off straight into a maximum-rate pull-out, and the zoom
 * would bleed the speed the aeroplane has not built yet and stall it back onto
 * the field it just left. (Measured, before this shared the height: a wingman
 * scrambling off Vert Galant reached 37 m, was handed to recovery, zoomed to
 * 175 m at 16 mph and fell out of it.)
 */
export function climbingOut(sim: SimState, tunables: Tunables): boolean {
  if (takingOff(sim)) return true;
  return sim.readout.altitudeAglM < tunables.aiGroundClearanceM && sim.readout.climbRateMps >= 0;
}

/**
 * The manual's own take-off, flown: full throttle throughout, stick forward to
 * bring the tail up once there is enough airflow for it, then back once the
 * wing can carry the aeroplane, then a *shallow* climb — "do not attempt to
 * climb at too steep of an angle or your aircraft will go into a stall (with no
 * room for recovery!)".
 *
 * The rudder is left alone during the roll on purpose: the wheels already track
 * the nose (`undercarriage.ts`), so there is no swing to hold off, and steering
 * against a heading the AI has no reason to prefer would only walk the flight
 * off its own aerodrome.
 */
export function takeoffInputs(sim: SimState, tunables: Tunables): FlightInputs {
  if (!sim.gear.onGround) {
    return inputs({
      throttle: 1,
      roll: rollToBank(sim, 0, tunables),
      pitch: pitchToAttitude(sim, CLIMB_OUT_PITCH_RAD, tunables),
    });
  }
  // Below the tail-up speed the stick does nothing useful anyway (control
  // authority is a `(V/Vfull)²` fade), so this is a shape rather than a schedule.
  const tailUp = sim.readout.airspeedMps < sim.config.spec.undercarriage.liftoffSpeedMps * TAIL_UP_SPEED_FRACTION;
  return inputs({ throttle: 1, pitch: tailUp ? -TAIL_UP_STICK : 0 });
}

/** Fraction of the liftoff speed at which the tail comes back down and the aeroplane is allowed to fly off. */
const TAIL_UP_SPEED_FRACTION = 0.85;

/**
 * How far forward the stick goes to bring the tail up — the manual's "be
 * careful to avoid pushing **too far** forward", which the AI now is.
 *
 * Full forward stick drives the ground attitude to `groundPitchRad × (1 −
 * gearTailLiftGain)`, i.e. half the three-point angle *below* level, and that is
 * past `gearPropStrikePitchDeg` on every airframe in the fleet: a wingman
 * holding the stick on the stop is a wingman with a bent propeller, and it only
 * ever got away with it because the pitch lag and the speed schedule ran out
 * first. Once each type sat at its own drawn attitude rather than a fleet-wide
 * 18° (`flight/undercarriage.ts`, 2026-08-25) there was less angle to travel and
 * more time to travel it in, and the scramble's wingman started nosing over on
 * its own aerodrome. Three quarters holds the fuselage a degree or two below
 * level on the flattest-sitting type in the fleet and leaves the propeller where
 * it belongs; the full-stop deflection is still there for a pilot who wants it,
 * which is what `flight-ground.test.ts`'s prop-strike acceptance test flies.
 */
const TAIL_UP_STICK = 0.75;

/** The manual's shallow climb-out. Well under its 20° best-climb figure, which is a cruise-speed number. */
const CLIMB_OUT_PITCH_RAD = 0.14;
